import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DogService } from '../../../services/dog.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BreedCategory, DogBreed } from '../../../models/data.models';
import { TranslationService } from '../../../services/translation.service';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';

@Component({
  selector: 'app-admin-dogs',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ReactiveFormsModule, PageHeaderComponent],
  templateUrl: './admin-dogs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDogsComponent {
  private dogService = inject(DogService);
  private translationService = inject(TranslationService);

  breedData = signal<BreedCategory[]>([]);
  isLoading = signal(true);

  // For adding new breed
  fb = inject(FormBuilder);
  breedForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    category: ['MAIN', Validators.required]
  });

  categories = [
    { value: 'MAIN', label: 'dogBreeds.mainBreeds' },
    { value: 'SPECIFIC_CROSS', label: 'dogBreeds.specificCrosses' },
    { value: 'OTHER', label: 'dogBreeds.other' }
  ];

  constructor() {
    this.loadBreeds();
  }

  async loadBreeds() {
    this.isLoading.set(true);
    const breeds = await this.dogService.getDogBreeds();

    // Group by category
    const mainBreeds = breeds.filter(b => b.category === 'MAIN');
    const crossBreeds = breeds.filter(b => b.category === 'SPECIFIC_CROSS');
    const otherBreeds = breeds.filter(b => b.category === 'OTHER');

    const grouped: BreedCategory[] = [];
    if (mainBreeds.length > 0) grouped.push({ titleKey: 'dogBreeds.mainBreeds', breeds: mainBreeds.map(b => b.name), rawBreeds: mainBreeds });
    if (crossBreeds.length > 0) grouped.push({ titleKey: 'dogBreeds.specificCrosses', breeds: crossBreeds.map(b => b.name), rawBreeds: crossBreeds });
    if (otherBreeds.length > 0) grouped.push({ titleKey: 'dogBreeds.other', breeds: otherBreeds.map(b => b.name), rawBreeds: otherBreeds });

    this.breedData.set(grouped);
    this.isLoading.set(false);
  }

  async addBreed() {
    if (this.breedForm.valid) {
      const formVal = this.breedForm.value;
      const newBreed = await this.dogService.addDogBreed({
        name: formVal.name,
        category: formVal.category
      });

      if (newBreed) {
        this.breedForm.reset({ category: 'MAIN' });
        this.loadBreeds();
      }
    }
  }

  async deleteBreed(breedName: string) {
    // Find the ID first (inefficient but works given the current structure, or I should store objects in the view)
    // Actually I updated 'grouped' to include 'rawBreeds' so I can iterate that in the template if I update the template.
    // For now, simpler to fetch the ID via the name from the cache or just refetch? 
    // Let's rely on finding it in the loaded list.
    const allBreeds = await this.dogService.getDogBreeds(); // or verify from local state
    const breed = allBreeds.find(b => b.name === breedName);
    if (breed && confirm(this.translationService.translate('common.deleteConfirm', breedName))) {
      await this.dogService.deleteDogBreed(breed.id);
      this.loadBreeds();
    }
  }
}
